import type { Cabecera } from '@features/seguimiento/cabecera';
import { Desenlace } from './desenlace';

/**
 * Lo que dice cada desenlace del pago (SPEC-013, Fase 2).
 *
 * **El prototipo no dibuja estas pantallas**: salta del pago al carnet. Se
 * componen con el lenguaje de `s_exito` —circulo con icono, titulo, explicacion
 * y una accion— que es la forma que SPEC-010 ya uso para contar el rechazo de la
 * solicitud, y con la misma pieza: `app-cabecera-estado` y su `Cabecera`.
 *
 * Aparte del componente, como en las otras pantallas del flujo: el copy se lee
 * junto y se prueba sin DOM. **Es propuesta hasta que la Escuela lo confirme**,
 * igual que los nueve textos de SPEC-010.
 */
export const CABECERAS: Record<Desenlace, Cabecera> = {
  /**
   * AC-6. **No se promete el carnet**: lo entrega SPEC-014, y prometerlo aqui
   * seria un boton al comodin.
   *
   * Tampoco se dice *"matriculado"*. Quien cierra el tramite es la Logic App del
   * pago, que sondea cada minuto y con `finished_approved` crea al alumno, la
   * matricula y el cupo; hay una ventana de hasta un minuto en la que el pago ya
   * esta y la matricula todavia no (AC-11).
   */
  aprobado: {
    titulo: '¡Listo, recibimos tu pago!',
    icono: 'ph-fill ph-check-circle',
    tono: 'exito',
    detalle: [
      {
        texto:
          'Estamos cerrando tu matrícula. El estado del trámite lo confirma en un momento; entra cuando quieras a mirarlo.',
      },
    ],
  },

  /**
   * AC-7. `CREATED` y `PENDING`: el pago existe y la confirmacion firmada aun no
   * llego. **No es un fallo**, y decirlo importa — es la diferencia entre
   * esperar tranquilo y volver a pagar por si acaso.
   */
  esperando: {
    titulo: 'Estamos confirmando tu pago',
    icono: 'ph-fill ph-clock',
    tono: 'espera',
    detalle: [
      {
        texto:
          'Tu banco todavía está procesando la transacción. Puede tardar un momento; quédate en esta pantalla mientras la consultamos.',
      },
    ],
  },

  /**
   * AC-8. **Lo primero que dice es que no hubo cobro**, porque es la primera
   * pregunta de cualquiera ante un pago rechazado y no responderla llena de
   * llamadas a la Escuela.
   *
   * Aqui caen tambien `EXPIRED` —un checkout vencido se puede reintentar y
   * tampoco cobro nada— y `REJECTED`, el que emite el middleware nuevo.
   */
  rechazado: {
    titulo: 'El pago no se completó',
    icono: 'ph-fill ph-x-circle',
    tono: 'detenida',
    detalle: [
      { texto: 'No te cobramos nada', fuerte: true },
      {
        texto:
          '. Tu banco no autorizó la transacción, así que puedes volver a intentarlo con el mismo medio de pago o con otro.',
      },
    ],
  },

  /**
   * AC-10. `ERROR`, un pago que no existe, o una consulta que no llego. **No se
   * cuenta como pago fallido**: se cuenta como que no se pudo saber, que es lo
   * que es — y por eso no ofrece reintentar, que podria cobrar dos veces.
   */
  error: {
    titulo: 'No pudimos confirmar tu pago',
    icono: 'ph-fill ph-warning-circle',
    tono: 'detenida',
    detalle: [
      {
        texto:
          'No logramos consultar cómo quedó la transacción. Tu trámite sigue guardado: entra al estado de tu matrícula y ahí verás si el pago quedó registrado.',
      },
    ],
  },
};

/** Mientras llega la primera respuesta del middleware. */
export const CONSULTANDO = 'Consultando cómo quedó tu pago…';

/** Entre una consulta y la siguiente, para que la espera no parezca colgada. */
export const SEGUIMOS_MIRANDO = 'Seguimos consultando. No cierres esta pantalla.';

/**
 * AC-7, la segunda mitad: **que hacer si sigue asi**.
 *
 * Se dice solo cuando el sondeo se agota. Antes seria adelantarse: lo normal es
 * que la confirmacion llegue en la primera o la segunda consulta.
 */
export const ESPERA_AGOTADA =
  'Tu banco se está tardando más de lo normal. Entra otra vez por el enlace de tu correo en unos minutos; si el cobro te aparece y aquí sigue sin confirmarse, escríbenos.';

/** La salida de las tres pantallas que no continuan solas (AC-6 y AC-10). */
export const IR_AL_ESTADO = 'Ver el estado de mi matrícula';

/**
 * AC-9. **Reabre el mismo pago**, no crea uno nuevo: por eso *"volver a
 * intentar"* y no *"pagar"*, que es lo que dice el resumen.
 */
export const REINTENTAR = 'Volver a intentar el pago';

/**
 * El `/retry` no salio. Puede ser la conexion, o que el pago ya sea terminal
 * —el middleware no reabre el checkout de uno que lo es, y eso es lo que
 * protege del doble cobro (AC-12)—. En los dos casos el estado del tramite es
 * donde se sale de dudas, y esta ahi mismo debajo.
 */
export const NO_SE_PUDO_REINTENTAR =
  'No pudimos reabrir el pago. Vuelve a intentarlo, o mira el estado de tu matrícula por si ya quedó registrado.';
